import { useCallback, useRef, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { doc } from '@atlaskit/adf-utils/builders';

import { useNotesData } from '@confluence/notes-util/entry-points/hooks/useNotesData';
import {
	useActiveNoteId,
	useNotesActions,
	useNoteEditorApi,
	type NoteEditorApi,
} from '@confluence/notes-util/entry-points/NotesStore';
import { useContentType, usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useCopyContentLink } from '@confluence/copy-content-link';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

import { generateQuote, appendAdf } from '../quoteToNoteHelper';

const handleErrors = (errors: any[]) => {
	errors.forEach((e) => {
		markErrorAsHandled(e);
		getMonitoringClient().submitError(e, {
			attribution: Attribution.PAGE_MODES,
		});
	});
};

export const useContextualNoteCreation = ({ source }: { source: string }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { createNote, updateNote, notesData } = useNotesData();
	const { setActiveNoteId } = useNotesActions();
	const [activeNoteId] = useActiveNoteId();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const [contentType] = useContentType();
	const isEditRoute = useIsEditorPage();
	const [{ noteEditorApi }] = useNoteEditorApi();
	const { makeContentLink } = useCopyContentLink({
		pageMode: isEditRoute ? 'edit' : 'view',
		contentType: contentType ?? '',
		contentId: contentId ?? '',
		spaceKey: spaceKey ?? '',
		source,
	});
	const pageUrl = makeContentLink() ?? '';

	// The two refs makes sure the hook tracks the latest state of the active note
	const noteEditorApiRef = useRef<NoteEditorApi | undefined>();
	const activeNoteIdRef = useRef<string | undefined>();
	useEffect(() => {
		noteEditorApiRef.current = noteEditorApi;
	}, [noteEditorApi]);

	useEffect(() => {
		activeNoteIdRef.current = activeNoteId;
	}, [activeNoteId]);

	const fireAnalyticsEvent = useCallback(
		({ actionSubjectId }: { actionSubjectId: string }) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
				},
			}).fire();
		},
		[createAnalyticsEvent, source],
	);

	const handleCreateNoteClick = useCallback(async () => {
		fireAnalyticsEvent({ actionSubjectId: 'createNote' });

		const activeSelection = window.getSelection();
		const highlightedText = activeSelection?.toString() || '';

		const { data, errors } = await createNote({
			variables: { body: JSON.stringify(generateQuote(highlightedText, pageUrl)) },
		});

		const createNoteErrors = errors || (data?.createNote?.errors as Error[]);
		if (createNoteErrors) {
			handleErrors(createNoteErrors);
		}

		setActiveNoteId(data?.createNote?.note?.ari);
	}, [fireAnalyticsEvent, createNote, setActiveNoteId, pageUrl]);

	const getNoteFromEditor = async () => {
		return new Promise((resolve) => {
			noteEditorApiRef.current?.core.actions.requestDocument((noteDoc) => {
				if (!noteDoc) {
					resolve(doc());
				}
				resolve(noteDoc);
			});
		});
	};

	const handleAddToNoteClick = useCallback(
		async (noteAri: string) => {
			fireAnalyticsEvent({ actionSubjectId: 'addToNote' });
			const activeSelection = window.getSelection();
			const highlightedText = activeSelection?.toString() || '';

			const quote = generateQuote(highlightedText, pageUrl);

			// Get existing note data to merge with
			const existingNoteAdf =
				activeNoteIdRef.current === noteAri
					? await getNoteFromEditor()
					: notesData.find((note: any) => note.ari === noteAri)?.adf;

			// Parse existing note body and merge with quote
			const mergedContent = appendAdf({
				existingAdf: existingNoteAdf ?? doc(),
				newAdf: quote,
			});

			if (activeNoteIdRef.current === noteAri) {
				noteEditorApiRef.current?.core.actions.replaceDocument(mergedContent);
			}

			const { data, errors } = await updateNote({
				variables: { body: JSON.stringify(mergedContent), ari: noteAri },
			});

			const updateNoteErrors = errors || data?.updateNote?.errors;
			if (updateNoteErrors) {
				handleErrors(updateNoteErrors);
			}

			if (activeNoteIdRef.current !== noteAri) {
				setActiveNoteId(noteAri);
			}
		},
		[fireAnalyticsEvent, setActiveNoteId, updateNote, pageUrl, notesData, activeNoteIdRef],
	);

	return { handleCreateNoteClick, handleAddToNoteClick };
};
